import { useMemo, useState } from 'react';
import {
  Box,
  Copy,
  Cuboid,
  Cylinder,
  Layers3,
  Move3D,
  Plus,
  QrCode,
  RotateCcw,
  Sparkles,
  Trash2,
  Upload,
} from 'lucide-react';

import Admin3DScene from './Admin3DScene';
import {
  DEFAULT_QR_PRODUCT_PRESET,
  QR_PRODUCT_PRESETS,
  getQrProductPresetDefinition,
  normalizePremiumDefinition,
} from './qrProductPresets';

import '../../../styles/QrPrint/QrPrint3DProductEditor.css';

const clone = (value) =>
  JSON.parse(JSON.stringify(value));

const makeId = (prefix = 'part') =>
  `${prefix}-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 7)}`;

const makePart = (type = 'box') => ({
  id: makeId('part'),
  name:
    type === 'cylinder'
      ? 'Cylinder'
      : 'Block',
  type,
  size:
    type === 'cylinder'
      ? [42, 18, 42]
      : [100, 24, 40],
  position: [0, 40, 0],
  rotation: [0, 0, 0],
  material: 'acrylic',
  color: '#ffffff',
  opacity: 1,
  radius: type === 'box' ? 5 : 0,
  role: 'structure',
});

function VectorFields({
  title,
  icon: Icon,
  values = [0, 0, 0],
  labels,
  step = 1,
  onChange,
}) {
  return (
    <div className="qr-print-3d-vector">
      <span className="qr-print-3d-vector__title">
        <Icon size={14} />
        {title}
      </span>

      <div className="qr-print-3d-vector__fields">
        {values.map((value, index) => (
          <label key={`${title}-${index}`}>
            <small>{labels[index]}</small>

            <input
              type="number"
              step={step}
              value={value}
              onChange={(event) =>
                onChange(index, event.target.value)
              }
            />
          </label>
        ))}
      </div>
    </div>
  );
}

export default function QrPrint3DProductEditor({
  value,
  onChange,
  source,
  onSourceChange,
  modelFile,
  modelUrl,
  onModelFileChange,
  onRemoveModel,
  modelPreview = null,
}) {
  // A selected or saved GLB always takes priority over placeholder preset geometry.
  // Keep the original block editor available for products without a GLB.
  const hasImportedModel = Boolean(modelFile || modelUrl);
  const showImportedModel = hasImportedModel || source === 'model';

  const definition = useMemo(
    () =>
      normalizePremiumDefinition(
        value,
        value?.preset_key ||
          DEFAULT_QR_PRODUCT_PRESET,
      ),
    [value],
  );

  const [selectedPartId, setSelectedPartId] =
    useState(definition.parts?.[0]?.id || null);

  const selectedPart =
    definition.parts?.find(
      (part) => part.id === selectedPartId,
    );

  const updateDefinition = (next) => {
    onChange?.({
      version: 2,
      design_system: 'premium_blocks',
      preset_key:
        next.preset_key ||
        definition.preset_key ||
        DEFAULT_QR_PRODUCT_PRESET,
      parts: next.parts || [],
      customization_zones:
        next.customization_zones || [],
    });
  };

  const applyPreset = (presetKey) => {
    const next =
      getQrProductPresetDefinition(presetKey);

    updateDefinition(next);
    setSelectedPartId(next.parts?.[0]?.id || null);
    onSourceChange?.('procedural');
  };

  const updatePart = (patch) => {
    if (!selectedPart) return;

    updateDefinition({
      ...definition,
      parts: definition.parts.map((part) =>
        part.id === selectedPartId
          ? {
              ...part,
              ...patch,
            }
          : part,
      ),
    });
  };

  const updateVector = (
    field,
    index,
    rawValue,
  ) => {
    if (!selectedPart) return;

    const nextVector = [
      ...(selectedPart[field] || [0, 0, 0]),
    ];

    nextVector[index] =
      Number(rawValue) || 0;

    updatePart({
      [field]: nextVector,
    });
  };

  const addPart = (type) => {
    const next = makePart(type);

    updateDefinition({
      ...definition,
      parts: [
        ...definition.parts,
        next,
      ],
    });

    setSelectedPartId(next.id);
  };

  const duplicatePart = () => {
    if (!selectedPart) return;

    const next = {
      ...clone(selectedPart),
      id: makeId('part'),
      name: `${selectedPart.name} copy`,
      position: [
        Number(selectedPart.position?.[0] || 0) + 12,
        Number(selectedPart.position?.[1] || 0),
        Number(selectedPart.position?.[2] || 0) + 12,
      ],
    };

    updateDefinition({
      ...definition,
      parts: [
        ...definition.parts,
        next,
      ],
    });

    setSelectedPartId(next.id);
  };

  const deletePart = () => {
    if (!selectedPart) return;

    const remaining =
      definition.parts.filter(
        (part) =>
          part.id !== selectedPart.id,
      );

    updateDefinition({
      ...definition,
      parts: remaining,
      customization_zones:
        definition.customization_zones?.filter(
          (zone) =>
            zone.part_id !== selectedPart.id,
        ) || [],
    });

    setSelectedPartId(
      remaining[0]?.id || null,
    );
  };

  const resetPreset = () => {
    applyPreset(
      definition.preset_key ||
        DEFAULT_QR_PRODUCT_PRESET,
    );
  };

  const qrZone =
    definition.customization_zones?.find(
      (zone) => zone.type === 'qr',
    );

  const updateQrZone = (
    field,
    index,
    rawValue,
  ) => {
    if (!qrZone) return;

    const next = [
      ...(qrZone[field] || []),
    ];

    next[index] =
      Number(rawValue) || 0;

    updateDefinition({
      ...definition,
      customization_zones:
        definition.customization_zones.map(
          (zone) =>
            zone.id === qrZone.id
              ? {
                  ...zone,
                  [field]: next,
                  locked: true,
                }
              : zone,
        ),
    });
  };

  return (
    <section className="qr-print-3d-editor">
      <div className="qr-print-3d-editor__topbar">
        <div>
          <span className="qr-print-3d-editor__eyebrow">
            <Sparkles size={15} />
            Premium 3D system
          </span>

          <h3>
            Build from reusable product blocks
          </h3>

          <p>
            Start from a commercial preset, then adjust the
            reusable structure. Restaurant owners only style
            the safe branding layer later.
          </p>
        </div>

        <div className="qr-print-3d-source-switch">
          <button
            type="button"
            className={
              !showImportedModel
                ? 'is-active'
                : ''
            }
            onClick={() =>
              onSourceChange?.('procedural')
            }
          >
            <Layers3 size={17} />
            Premium blocks
          </button>

          <button
            type="button"
            className={
              source === 'model'
                ? 'is-active'
                : ''
            }
            onClick={() =>
              onSourceChange?.('model')
            }
          >
            <Upload size={17} />
            Upload GLB
          </button>
        </div>
      </div>

      {showImportedModel ? (
        <div className="qr-print-3d-editor__layout qr-print-3d-editor__layout--model">
          <aside className="qr-print-3d-editor__sidebar">
            <div className="qr-print-3d-editor__section">
              <strong>Imported model data</strong>
              <p className="qr-print-3d-editor__model-hint">
                {modelFile?.name || (modelUrl ? 'Saved product model' : 'No GLB uploaded yet')}
              </p>
              <small>
                The model is read directly from your GLB, preserving its meshes, materials, textures and proportions.
              </small>
              {modelUrl && !modelFile ? (
                <a href={modelUrl} target="_blank" rel="noreferrer">Open saved GLB</a>
              ) : null}
            </div>
            <div className="qr-print-3d-editor__section">
              <div className="qr-print-3d-editor__section-heading">
                <strong>Protected design zones</strong>
              </div>
              <div className="qr-print-3d-zone-summary">
                {(definition.customization_zones || []).map((zone) => (
                  <span key={zone.id}><QrCode size={14} />{zone.type}</span>
                ))}
              </div>
              {qrZone ? (
                <>
                  <VectorFields
                    title="QR zone position"
                    icon={Move3D}
                    values={qrZone.position || [0, 0, 0]}
                    labels={['X', 'Y', 'Z']}
                    step={0.1}
                    onChange={(index, next) => updateQrZone('position', index, next)}
                  />
                  <VectorFields
                    title="QR zone size"
                    icon={Box}
                    values={qrZone.size || [30, 30]}
                    labels={['Width', 'Height']}
                    step={0.1}
                    onChange={(index, next) => updateQrZone('size', index, next)}
                  />
                </>
              ) : (
                <small>No QR zone is defined in this preset.</small>
              )}
              <small>
                QR zone settings are saved with the product definition.
                Imported mesh geometry and materials are not modified by these fields.
              </small>
            </div>
          </aside>
          <div className="qr-print-3d-editor__canvas-panel">
            <div className="qr-print-3d-editor__canvas-toolbar">
              <div>
                <strong>Live product structure</strong>
                <span>Imported GLB geometry · Drag to rotate · Scroll to zoom</span>
              </div>
            </div>
            {modelPreview || (
              <div className="qr-print-3d-editor__model-empty" role="status">
                <Box size={32} />
                <strong>No model selected</strong>
                <span>Upload a GLB using the model field above to view it here.</span>
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="qr-print-premium-presets">
            {Object.values(
              QR_PRODUCT_PRESETS,
            ).map((preset) => (
              <button
                key={preset.key}
                type="button"
                className={
                  definition.preset_key === preset.key
                    ? 'is-active'
                    : ''
                }
                onClick={() =>
                  applyPreset(preset.key)
                }
              >
                <span>
                  <Box size={18} />
                </span>

                <strong>
                  {preset.label}
                </strong>

                <small>
                  {preset.description}
                </small>
              </button>
            ))}
          </div>

          <div className="qr-print-3d-editor__layout">
            <aside className="qr-print-3d-editor__sidebar">
              <div className="qr-print-3d-editor__section">
                <div className="qr-print-3d-editor__section-heading">
                  <strong>
                    Reusable blocks
                  </strong>

                  <div>
                    <button
                      type="button"
                      onClick={() =>
                        addPart('box')
                      }
                    >
                      <Plus size={15} />
                      Block
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        addPart('cylinder')
                      }
                    >
                      <Cylinder size={15} />
                    </button>
                  </div>
                </div>

                <div className="qr-print-3d-part-list">
                  {definition.parts.map(
                    (part) => (
                      <button
                        type="button"
                        key={part.id}
                        className={
                          part.id === selectedPartId
                            ? 'is-active'
                            : ''
                        }
                        onClick={() =>
                          setSelectedPartId(part.id)
                        }
                      >
                        <Cuboid size={16} />

                        <span>
                          {part.name}
                        </span>

                        <small>
                          {part.role || part.type}
                        </small>
                      </button>
                    ),
                  )}
                </div>
              </div>

              {selectedPart ? (
                <div className="qr-print-3d-editor__section">
                  <div className="qr-print-3d-editor__section-heading">
                    <strong>
                      Selected block
                    </strong>

                    <div>
                      <button
                        type="button"
                        title="Duplicate"
                        onClick={duplicatePart}
                      >
                        <Copy size={15} />
                      </button>

                      <button
                        type="button"
                        className="is-danger"
                        title="Delete"
                        onClick={deletePart}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>

                  <label>
                    <span>Name</span>

                    <input
                      value={selectedPart.name || ''}
                      onChange={(event) =>
                        updatePart({
                          name: event.target.value,
                        })
                      }
                    />
                  </label>

                  <label>
                    <span>Role</span>

                    <select
                      value={
                        selectedPart.role ||
                        'structure'
                      }
                      onChange={(event) =>
                        updatePart({
                          role: event.target.value,
                        })
                      }
                    >
                      <option value="structure">
                        Structure
                      </option>
                      <option value="base">
                        Base
                      </option>
                      <option value="support">
                        Support
                      </option>
                      <option value="face">
                        Face
                      </option>
                      <option value="print_surface">
                        Print surface
                      </option>
                      <option value="accent">
                        Accent
                      </option>
                    </select>
                  </label>

                  <label>
                    <span>Material</span>

                    <select
                      value={
                        selectedPart.material ||
                        'acrylic'
                      }
                      onChange={(event) =>
                        updatePart({
                          material:
                            event.target.value,
                        })
                      }
                    >
                      <option value="acrylic">
                        Acrylic
                      </option>
                      <option value="wood">
                        Wood
                      </option>
                      <option value="metal">
                        Metal
                      </option>
                      <option value="plastic">
                        Printed insert
                      </option>
                      <option value="rubber">
                        Rubber
                      </option>
                    </select>
                  </label>

                  <label>
                    <span>Color</span>

                    <input
                      type="color"
                      value={
                        selectedPart.color ||
                        '#ffffff'
                      }
                      onChange={(event) =>
                        updatePart({
                          color:
                            event.target.value,
                        })
                      }
                    />
                  </label>

                  <label>
                    <span>
                      Corner radius
                    </span>

                    <input
                      type="range"
                      min="0"
                      max="18"
                      step="1"
                      value={
                        selectedPart.radius || 0
                      }
                      onChange={(event) =>
                        updatePart({
                          radius:
                            Number(
                              event.target.value,
                            ),
                        })
                      }
                    />
                  </label>

                  <VectorFields
                    title="Size (mm)"
                    icon={Cuboid}
                    values={
                      selectedPart.size
                    }
                    labels={['W', 'H', 'D']}
                    onChange={(index, value) =>
                      updateVector(
                        'size',
                        index,
                        value,
                      )
                    }
                  />

                  <VectorFields
                    title="Position (mm)"
                    icon={Move3D}
                    values={
                      selectedPart.position
                    }
                    labels={['X', 'Y', 'Z']}
                    onChange={(index, value) =>
                      updateVector(
                        'position',
                        index,
                        value,
                      )
                    }
                  />

                  <VectorFields
                    title="Rotation (°)"
                    icon={RotateCcw}
                    values={
                      selectedPart.rotation
                    }
                    labels={['X', 'Y', 'Z']}
                    onChange={(index, value) =>
                      updateVector(
                        'rotation',
                        index,
                        value,
                      )
                    }
                  />
                </div>
              ) : null}

              <div className="qr-print-3d-editor__section">
                <strong>
                  Protected design zones
                </strong>

                <div className="qr-print-3d-zone-summary">
                  {(definition.customization_zones || []).map(
                    (currentZone) => (
                      <span key={currentZone.id}>
                        <QrCode size={14} />
                        {currentZone.type}
                      </span>
                    ),
                  )}
                </div>

                <small>
                  QR is mandatory. Brand and CTA zones are reused
                  by manual customization and AI styling.
                </small>
              </div>
            </aside>

            <div className="qr-print-3d-editor__canvas-panel">
              <div className="qr-print-3d-editor__canvas-toolbar">
                <div>
                  <strong>
                    Live product structure
                  </strong>

                  <span>
                    Click a block to edit it.
                  </span>
                </div>

                <button
                  type="button"
                  onClick={resetPreset}
                >
                  <RotateCcw size={15} />
                  Reset preset
                </button>
              </div>

              <Admin3DScene
                definition={definition}
                selectedPartId={
                  selectedPartId
                }
                onSelectPart={
                  setSelectedPartId
                }
              />
            </div>
          </div>
        </>
      )}
    </section>
  );
}